import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { PageHeader, PrimaryButton, Modal, Field, TextInput, Select, Textarea, StatusPill, useFetch, Pagination } from "../../components/crm/ui";
import WhatsAppTracker from "../../components/crm/WhatsAppTracker";
import { CustomerPicker } from "../../components/crm/CustomerPicker";
import {
  Plus, Sparkles, ArrowRightCircle, Trash2, RefreshCw, Mail, Phone, MapPin,
  Calendar, FileText, User, Building2, ChevronUp, ChevronDown, Search, Filter, X, UserCheck, MessageCircle,
  Zap, Lightbulb, BatteryCharging, HelpCircle, FileSignature, AlertTriangle,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area,
} from "recharts";

const CATEGORIES = ["hot", "warm", "cold"];
const STATUSES = ["open", "accepted", "on_hold", "dropped", "converted"];

// Enquiry category — decides the Lead Type (EPC → Opportunity pipeline, Product Enquiry → Quotation only)
const LEAD_ENQUIRY_CATEGORIES = [
  { value: "residential", label: "Residential", Icon: User },
  { value: "commercial", label: "Commercial", Icon: Building2 },
  { value: "housing_society", label: "Housing Society", Icon: Building2 },
  { value: "ev_charger", label: "EV Charger", Icon: Zap },
  { value: "street_light", label: "Street Light", Icon: Lightbulb },
  { value: "battery_storage", label: "Battery Storage", Icon: BatteryCharging },
  { value: "others", label: "Others", Icon: HelpCircle },
];
const LEAD_CATEGORY_LABEL = Object.fromEntries(LEAD_ENQUIRY_CATEGORIES.map((c) => [c.value, c.label]));
const LEAD_TYPE_LABEL = { epc: "EPC", product_enquiry: "Product Enquiry" };
const LEAD_TYPE_TONE = {
  epc: "bg-sky-50 text-sky-800 border border-sky-200",
  product_enquiry: "bg-purple-50 text-purple-800 border border-purple-200",
};
const OPP_STATUS_LABEL = { open: "Open", active: "Active", won: "Won", won_verbal: "Won Verbal", lost: "Lost", on_hold: "On Hold", dropped: "Dropped" };
const QUOTE_STATUS_LABEL_MAP = {
  draft: "Draft", sent: "Sent", customer_evaluation_wip: "Customer Evaluation",
  verbally_confirmed_accepted: "Verbally Confirmed", reprocessing_required: "Re-Processing",
  accepted: "Accepted", rejected: "Rejected",
};
const OPP_STATUS_TONE = { won: "success", won_verbal: "success", lost: "danger", dropped: "danger", on_hold: "warning" };
const QUOTE_STATUS_TONE_MAP = { accepted: "success", rejected: "danger" };

function followOnLabel(lead) {
  if (!lead.follow_on_type) return null;
  return lead.follow_on_type === "opportunity"
    ? (OPP_STATUS_LABEL[lead.follow_on_status] || lead.follow_on_status)
    : (QUOTE_STATUS_LABEL_MAP[lead.follow_on_status] || lead.follow_on_status);
}
function followOnTone(lead) {
  const map = lead.follow_on_type === "opportunity" ? OPP_STATUS_TONE : QUOTE_STATUS_TONE_MAP;
  return map[lead.follow_on_status] || "info";
}
const EPC_CATEGORIES_FE = new Set(["residential", "commercial", "housing_society"]);
function derive_lead_type_fe(cat) { return EPC_CATEGORIES_FE.has(cat) ? "epc" : "product_enquiry"; }

const CHART_COLORS = ["#1F4E2C", "#F39C12", "#0EA5E9", "#94A3B8", "#7C3AED", "#DC2626"];
const monthLabel = (m) => {
  if (!m) return "—";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
};

function KpiChip({ label, value, tone = "bg-slate-100 text-slate-800" }) {
  return (
    <div className={"px-2.5 py-1.5 flex items-center gap-1.5 " + tone}>
      <div className="font-display text-[16px] font-bold leading-none tabular-nums">{value}</div>
      <div className="font-mono text-[9px] uppercase tracking-widest leading-none">{label}</div>
    </div>
  );
}
function ChartCard({ title, children }) {
  return (
    <div className="border border-[#E2E4DC] bg-white p-3">
      <div className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-[#4A5568] mb-2">{title}</div>
      <div style={{ width: "100%", height: 160 }}>{children}</div>
    </div>
  );
}

const SOURCE_FILTERS = [
  { value: "all", label: "All sources" },
  { value: "crm", label: "CRM" },
  { value: "public_quote", label: "Quote calculator" },
  { value: "campaign", label: "Campaign" },
  { value: "whatsapp_chat", label: "WhatsApp" },
  { value: "sc_referral", label: "SC Referral" },
  { value: "public_signup", label: "Self-signup" },
  { value: "public_app", label: "Customer app" },
];

const SOURCE_LABEL = {
  crm: "CRM",
  public_quote: "Quote",
  campaign: "Campaign",
  public_survey: "Campaign",
  whatsapp_chat: "WhatsApp",
  sc_referral: "SC",
  public_signup: "Self-signup",
  public_app: "App",
};

// Status dot color (small left-side indicator next to inline Select)
const STATUS_DOT = {
  open: "bg-sky-500",
  accepted: "bg-emerald-500",
  on_hold: "bg-amber-500",
  dropped: "bg-red-500",
  converted: "bg-[#1F4E2C]",
};
const STATUS_TONE = { open: "info", accepted: "success", dropped: "danger", on_hold: "warning", converted: "default" };
const CATEGORY_TONE = { hot: "danger", warm: "warning", cold: "info" };

const EMPTY = {
  name: "", customer_id: "", avg_power_consumption: 0, expected_installation_date: "",
  category: "warm", notes: "", sales_employee_id: "",
  lead_category: "residential", lead_category_other_text: "",
};
const EMPTY_NEW_CUSTOMER = { customer_name: "", customer_type: "residential", phone: "", email: "", address: "", pin_code: "" };

const SERVICEABILITY_REASON_LABEL = {
  district_not_permitted: "District not in permissible list",
  pin_excluded: "PIN code excluded from service area",
  no_pin_on_file: "No PIN on file",
  invalid_pin: "Invalid PIN code",
  api_unavailable_fail_open: "PIN lookup unavailable",
  serviceable: "Serviceable",
};

function NotServiceableBadge({ lead, className = "" }) {
  if (lead.is_serviceable !== false) return null;
  return (
    <span
      className={"inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider bg-red-100 text-red-700 px-1.5 py-0.5 border border-red-200 " + className}
      data-testid={`not-serviceable-badge-${lead.id}`}
      title={SERVICEABILITY_REASON_LABEL[lead.service_check_reason] || lead.service_check_reason || ""}
    >
      <AlertTriangle className="w-2.5 h-2.5" /> Not Serviceable
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso).getTime();
  const diff = (Date.now() - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return fmtDate(iso);
}

export default function Leads() {
  const nav = useNavigate();
  const { data: leads, reload } = useFetch("/leads");
  const { data: customers } = useFetch("/customers");
  const { data: employees } = useFetch("/sales-employees");
  const [scUsers, setScUsers] = useState([]);

  // Pull active SCs once (admin-only endpoint)
  useEffect(() => {
    api.get("/admin/sc").then((r) => setScUsers(r.data || [])).catch(() => {});
  }, []);
  const scMap = useMemo(() => {
    const m = {};
    scUsers.forEach((u) => { m[u.id] = { ...u, full_name: u.full_name || u.name }; });
    return m;
  }, [scUsers]);

  // Create modal
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [customerMode, setCustomerMode] = useState("existing"); // 'existing' | 'new'
  const [newCust, setNewCust] = useState(EMPTY_NEW_CUSTOMER);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Detail modal
  const [detailLead, setDetailLead] = useState(null);
  const [detailTab, setDetailTab] = useState("details");

  // Filters
  const [filterSource, setFilterSource] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterAssigned, setFilterAssigned] = useState("all");
  const [filterSc, setFilterSc] = useState("all");           // 'all' | 'any' | <sc_user_id>
  const [filterAppt, setFilterAppt] = useState("all");        // 'all' | 'today' | 'week' | 'month' | 'overdue'
  const [filterType, setFilterType] = useState("all");        // 'all' | 'epc' | 'product_enquiry'
  const [filterLeadCat, setFilterLeadCat] = useState("all");  // 'all' | residential/commercial/.../others
  const [phoneSearch, setPhoneSearch] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");         // YYYY-MM-DD
  const [createdTo, setCreatedTo] = useState("");
  const [search, setSearch] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Sort
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [lastSync, setLastSync] = useState(Date.now());

  // Auto-poll every 20s
  useEffect(() => {
    const t = setInterval(() => { reload(); setLastSync(Date.now()); }, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function startCreate() { setForm(EMPTY); setCustomerMode("existing"); setNewCust(EMPTY_NEW_CUSTOMER); setSelectedCustomer(null); setOpen(true); }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, avg_power_consumption: Number(form.avg_power_consumption) };
    if (!payload.sales_employee_id) delete payload.sales_employee_id;
    if (!payload.expected_installation_date) delete payload.expected_installation_date;
    if (payload.lead_category !== "others") payload.lead_category_other_text = "";
    if (customerMode === "new") {
      delete payload.customer_id;
      payload.new_customer = { ...newCust };
    } else {
      delete payload.new_customer;
    }
    try {
      await api.post("/leads", payload);
      toast.success("Lead created — sales employee notified by email");
      setOpen(false); reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  }

  async function changeStatus(lead, status) {
    if (lead.status === status) return;
    try {
      await api.put(`/leads/${lead.id}/status?status=${status}`);
      toast.success(`Status: ${status}`);
      reload();
      if (detailLead?.id === lead.id) setDetailLead({ ...detailLead, status });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  async function convert(lead) {
    if (lead.status !== "accepted") {
      toast.error("Lead must be 'Accepted' before converting");
      return;
    }
    if (!lead.sales_employee_id) {
      toast.error("Assign a Sales Employee to the lead first");
      return;
    }
    if (lead.is_serviceable === false) {
      toast.error("This lead is 'Not Serviceable' — its PIN falls outside permissible territories and cannot be converted to an Opportunity.");
      return;
    }
    if (!window.confirm(`Convert "${lead.name}" to opportunity? Activities will be auto-created.`)) return;
    try {
      const { data } = await api.post(`/leads/${lead.id}/convert`);
      toast.success(`Opportunity created: ${data.name}`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  async function createQuotationFromLead(lead) {
    if (lead.status !== "accepted") {
      toast.error("Lead must be 'Accepted' before creating a quotation");
      return;
    }
    if (lead.is_serviceable === false) {
      toast.error("This lead is 'Not Serviceable' — its PIN falls outside permissible territories and cannot be converted to a Quotation.");
      return;
    }
    if (!window.confirm(`Create a draft quotation for "${lead.name}"?`)) return;
    try {
      const { data } = await api.post(`/leads/${lead.id}/convert-to-quotation`);
      toast.success(`Quotation created: ${data.quotation_number}`);
      reload();
      nav(`/crm/quotations/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  async function remove(l) {
    if (!window.confirm(`Delete lead ${l.name}?`)) return;
    await api.delete(`/leads/${l.id}`); toast.success("Deleted"); reload();
  }

  // ── Filtering + sorting ──────────────────────────────────────────────
  const sourceCounts = leads.reduce((acc, l) => {
    let s = l.source || "crm";
    if (s === "public_survey") s = "campaign";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const phoneQ = phoneSearch.trim().replace(/\s|-/g, "");
    const fromTs = createdFrom ? new Date(`${createdFrom}T00:00:00`).getTime() : null;
    const toTs = createdTo ? new Date(`${createdTo}T23:59:59`).getTime() : null;
    const now = Date.now();
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const todayStart = today.getTime();
    const tomorrowStart = todayStart + 86400000;
    const weekEnd = todayStart + 86400000 * 7;
    const monthEnd = todayStart + 86400000 * 31;

    return leads.filter((l) => {
      let s = l.source || "crm";
      if (s === "public_survey") s = "campaign";
      if (filterSource !== "all" && s !== filterSource) return false;
      if (filterStatus !== "all" && l.status !== filterStatus) return false;
      if (filterCategory !== "all" && l.category !== filterCategory) return false;
      if (filterType !== "all" && (l.lead_type || derive_lead_type_fe(l.lead_category)) !== filterType) return false;
      if (filterLeadCat !== "all" && l.lead_category !== filterLeadCat) return false;
      if (filterAssigned !== "all") {
        if (filterAssigned === "unassigned" && l.sales_employee_id) return false;
        if (filterAssigned !== "unassigned" && l.sales_employee_id !== filterAssigned) return false;
      }
      // SC filter
      if (filterSc === "any" && !l.sc_user_id) return false;
      if (filterSc !== "all" && filterSc !== "any" && l.sc_user_id !== filterSc) return false;

      // Phone search
      if (phoneQ) {
        const p = (l.customer_snapshot?.phone || "").replace(/\s|-/g, "");
        if (!p.includes(phoneQ)) return false;
      }

      // Created date range
      if (fromTs || toTs) {
        const t = l.created_at ? new Date(l.created_at).getTime() : 0;
        if (fromTs && t < fromTs) return false;
        if (toTs && t > toTs) return false;
      }

      // Appointment window
      if (filterAppt !== "all") {
        const a = l.appointment_at ? new Date(l.appointment_at).getTime() : null;
        if (!a) return false;
        if (filterAppt === "today" && (a < todayStart || a >= tomorrowStart)) return false;
        if (filterAppt === "week" && (a < todayStart || a >= weekEnd)) return false;
        if (filterAppt === "month" && (a < todayStart || a >= monthEnd)) return false;
        if (filterAppt === "overdue" && a >= now) return false;
      }

      if (q) {
        const hay = [
          l.name, l.notes,
          l.customer_snapshot?.customer_name,
          l.customer_snapshot?.email,
          l.customer_snapshot?.phone,
          scMap[l.sc_user_id]?.full_name,
        ].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [leads, filterSource, filterStatus, filterCategory, filterType, filterLeadCat, filterAssigned, filterSc, filterAppt, phoneSearch, createdFrom, createdTo, search, scMap]);

  const kpis = useMemo(() => {
    const acc = { total: filtered.length, epc: 0, product_enquiry: 0, open: 0, accepted: 0, converted: 0, on_hold: 0, dropped: 0, hot: 0 };
    for (const l of filtered) {
      const t = l.lead_type || derive_lead_type_fe(l.lead_category);
      acc[t] = (acc[t] || 0) + 1;
      acc[l.status] = (acc[l.status] || 0) + 1;
      if (l.category === "hot") acc.hot += 1;
    }
    return acc;
  }, [filtered]);

  const analytics = useMemo(() => {
    const byStatus = {}; const byType = {}; const byCategory = {}; const byMonth = {};
    for (const l of filtered) {
      byStatus[l.status] = (byStatus[l.status] || 0) + 1;
      const t = l.lead_type || derive_lead_type_fe(l.lead_category);
      byType[t] = (byType[t] || 0) + 1;
      const cat = l.lead_category || "residential";
      byCategory[cat] = (byCategory[cat] || 0) + 1;
      const m = (l.created_at || "").slice(0, 7);
      if (m) byMonth[m] = (byMonth[m] || 0) + 1;
    }
    const statusData = Object.entries(byStatus).map(([name, value]) => ({ name: name.replace("_", " "), value }));
    const typeData = Object.entries(byType).map(([k, value]) => ({ name: LEAD_TYPE_LABEL[k] || k, value }));
    const categoryData = LEAD_ENQUIRY_CATEGORIES.map((c) => ({ name: c.label, value: byCategory[c.value] || 0 })).filter((d) => d.value > 0);
    const months = Object.keys(byMonth).sort().slice(-6);
    const trendData = months.map((m) => ({ month: monthLabel(m), count: byMonth[m] }));
    return { statusData, typeData, categoryData, trendData };
  }, [filtered]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av, bv;
      switch (sortKey) {
        case "name": av = a.name || ""; bv = b.name || ""; break;
        case "customer": av = a.customer_snapshot?.customer_name || ""; bv = b.customer_snapshot?.customer_name || ""; break;
        case "category": av = a.category; bv = b.category; break;
        case "status": av = a.status; bv = b.status; break;
        case "power": av = a.avg_power_consumption || 0; bv = b.avg_power_consumption || 0; break;
        default: av = a.created_at || ""; bv = b.created_at || "";
      }
      const cmp = av > bv ? 1 : av < bv ? -1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  function toggleSort(k) {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("desc"); }
  }

  function clearFilters() {
    setFilterSource("all"); setFilterStatus("all"); setFilterCategory("all"); setFilterAssigned("all");
    setFilterSc("all"); setFilterAppt("all"); setFilterType("all"); setFilterLeadCat("all");
    setPhoneSearch(""); setCreatedFrom(""); setCreatedTo(""); setSearch("");
    setPage(1);
  }
  const hasFilters =
    filterSource !== "all" || filterStatus !== "all" || filterCategory !== "all" || filterAssigned !== "all" ||
    filterSc !== "all" || filterAppt !== "all" || filterType !== "all" || filterLeadCat !== "all" ||
    phoneSearch.trim() || createdFrom || createdTo || search.trim();

  // Reset to page 1 whenever filters/search/sort change
  useEffect(() => { setPage(1); },
    [filterSource, filterStatus, filterCategory, filterType, filterLeadCat, filterAssigned, filterSc, filterAppt, phoneSearch, createdFrom, createdTo, search, sortKey, sortDir, pageSize]);

  const paged = useMemo(() => {
    const startIdx = (page - 1) * pageSize;
    return sorted.slice(startIdx, startIdx + pageSize);
  }, [sorted, page, pageSize]);

  function SortHeader({ k, children, className = "" }) {
    const active = sortKey === k;
    return (
      <button
        type="button"
        onClick={() => toggleSort(k)}
        data-testid={`sort-${k}`}
        className={"inline-flex items-center gap-1 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569] hover:text-[#0F1A12] " + className}
      >
        {children}
        {active && (sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      </button>
    );
  }

  return (
    <div data-testid="leads-page">
      <PageHeader
        overline="Pipeline"
        title="Leads"
        action={<PrimaryButton onClick={startCreate} testid="create-lead-btn"><Plus className="w-4 h-4" /> New Lead</PrimaryButton>}
      />

      {/* ── KPI strip ──────────────────────────────────────────── */}
      <div className="flex items-center gap-1 mb-3 flex-wrap" data-testid="leads-kpi-strip">
        <KpiChip label="Total" value={kpis.total} tone="bg-[#1F4E2C] text-white" />
        <KpiChip label="EPC" value={kpis.epc} tone="bg-sky-50 text-sky-800" />
        <KpiChip label="Product Enquiry" value={kpis.product_enquiry} tone="bg-purple-50 text-purple-800" />
        <KpiChip label="Open" value={kpis.open || 0} />
        <KpiChip label="Accepted" value={kpis.accepted || 0} tone="bg-amber-50 text-amber-800" />
        <KpiChip label="Converted" value={kpis.converted || 0} tone="bg-emerald-50 text-emerald-800" />
        <KpiChip label="On Hold / Dropped" value={(kpis.on_hold || 0) + (kpis.dropped || 0)} tone="bg-red-50 text-red-700" />
        <KpiChip label="Hot Leads" value={kpis.hot} tone="bg-orange-50 text-orange-800" />
      </div>

      {/* ── Executive charts ───────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="leads-analytics">
        <ChartCard title="Status Mix">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={analytics.statusData} dataKey="value" nameKey="name" innerRadius={35} outerRadius={55} paddingAngle={2}>
                {analytics.statusData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 11, fontFamily: "monospace" }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="EPC vs Product Enquiry">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={analytics.typeData} dataKey="value" nameKey="name" innerRadius={35} outerRadius={55} paddingAngle={2}>
                {analytics.typeData.map((_, i) => <Cell key={i} fill={CHART_COLORS[(i + 2) % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 11, fontFamily: "monospace" }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="By Category">
          <ResponsiveContainer>
            <BarChart data={analytics.categoryData} layout="vertical" margin={{ left: 4, right: 8, top: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 2" stroke="#EEF2EA" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 9, fontFamily: "monospace" }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={78} tick={{ fontSize: 8.5, fontFamily: "monospace" }} />
              <Tooltip contentStyle={{ fontSize: 11, fontFamily: "monospace" }} />
              <Bar dataKey="value" fill="#1F4E2C" barSize={11} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Leads Trend (6mo)">
          <ResponsiveContainer>
            <AreaChart data={analytics.trendData} margin={{ left: -20, right: 8, top: 4, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 2" stroke="#EEF2EA" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 9, fontFamily: "monospace" }} />
              <YAxis tick={{ fontSize: 9, fontFamily: "monospace" }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 11, fontFamily: "monospace" }} />
              <Area type="monotone" dataKey="count" stroke="#1F4E2C" fill="#DDE9E0" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* ── Filter bar ─────────────────────────────────────────── */}
      <div className="bg-white border border-[#DDE5D8] mb-4">
        <div className="px-4 py-3 border-b border-[#DDE5D8] flex items-center gap-2 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-[#475569]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#475569]">Filters</span>

          <div className="relative ml-2">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, customer, email, phone…"
              data-testid="leads-search"
              className="pl-8 pr-3 py-1.5 text-xs font-mono border border-[#DDE5D8] focus:border-[#1F4E2C] outline-none bg-white w-72"
            />
          </div>

          <select
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            data-testid="filter-source"
            className="text-xs font-mono border border-[#DDE5D8] px-2.5 py-1.5 bg-white focus:border-[#1F4E2C] outline-none"
          >
            {SOURCE_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label} {f.value !== "all" ? `(${sourceCounts[f.value] || 0})` : `(${leads.length})`}
              </option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="filter-status"
            className="text-xs font-mono border border-[#DDE5D8] px-2.5 py-1.5 bg-white focus:border-[#1F4E2C] outline-none"
          >
            <option value="all">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>

          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            data-testid="filter-category"
            className="text-xs font-mono border border-[#DDE5D8] px-2.5 py-1.5 bg-white focus:border-[#1F4E2C] outline-none"
          >
            <option value="all">All temperatures</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            data-testid="filter-lead-type"
            className="text-xs font-mono border border-[#DDE5D8] px-2.5 py-1.5 bg-white focus:border-[#1F4E2C] outline-none"
          >
            <option value="all">All lead types</option>
            <option value="epc">EPC</option>
            <option value="product_enquiry">Product Enquiry</option>
          </select>

          <select
            value={filterAssigned}
            onChange={(e) => setFilterAssigned(e.target.value)}
            data-testid="filter-assigned"
            className="text-xs font-mono border border-[#DDE5D8] px-2.5 py-1.5 bg-white focus:border-[#1F4E2C] outline-none"
          >
            <option value="all">All assignees</option>
            <option value="unassigned">— Unassigned —</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>

          {hasFilters && (
            <button
              onClick={clearFilters}
              data-testid="clear-filters"
              className="text-[10px] font-mono uppercase tracking-widest text-[#1F4E2C] hover:underline inline-flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}

          <button
            onClick={() => setAdvancedOpen((v) => !v)}
            data-testid="leads-advanced-toggle"
            className="text-[10px] font-mono uppercase tracking-widest text-[#475569] hover:text-[#1F4E2C] inline-flex items-center gap-1"
          >
            {advancedOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} Advanced
          </button>

          <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-[#475569]">
            <span className="text-[#0F1A12] font-semibold" data-testid="leads-result-count">{sorted.length}</span> / {leads.length} leads
          </span>

          <button
            onClick={() => { reload(); setLastSync(Date.now()); toast.success("Leads refreshed"); }}
            data-testid="leads-refresh-btn"
            title="Refresh"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-widest border border-[#DDE5D8] text-[#475569] hover:border-[#1F4E2C] hover:text-[#1F4E2C] transition-colors"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          <span
            className="font-mono text-[9px] uppercase tracking-widest text-[#94A3B8] flex items-center gap-1.5"
            data-testid="leads-live-indicator"
            title={`Auto-syncing every 20s · last sync ${new Date(lastSync).toLocaleTimeString()}`}
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
            </span>
            Live
          </span>
        </div>

        {/* ── Advanced filters (collapsible) ───────────────────────── */}
        {advancedOpen && (
          <div
            className="px-4 py-3 border-b border-[#DDE5D8] bg-[#FAFBF7] grid grid-cols-1 md:grid-cols-4 gap-3"
            data-testid="leads-advanced-row"
          >
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569] mb-1">Phone</div>
              <div className="relative">
                <Phone className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <input
                  type="text"
                  value={phoneSearch}
                  onChange={(e) => setPhoneSearch(e.target.value)}
                  placeholder="+91 …"
                  data-testid="leads-phone-search"
                  className="w-full pl-7 pr-2 py-1.5 text-xs font-mono border border-[#DDE5D8] focus:border-[#1F4E2C] outline-none bg-white"
                />
              </div>
            </div>

            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569] mb-1">Created · from</div>
              <input
                type="date"
                value={createdFrom}
                onChange={(e) => setCreatedFrom(e.target.value)}
                data-testid="leads-created-from"
                className="w-full px-2 py-1.5 text-xs font-mono border border-[#DDE5D8] focus:border-[#1F4E2C] outline-none bg-white"
              />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569] mb-1">Created · to</div>
              <input
                type="date"
                value={createdTo}
                onChange={(e) => setCreatedTo(e.target.value)}
                data-testid="leads-created-to"
                className="w-full px-2 py-1.5 text-xs font-mono border border-[#DDE5D8] focus:border-[#1F4E2C] outline-none bg-white"
              />
            </div>

            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569] mb-1">Appointment</div>
              <select
                value={filterAppt}
                onChange={(e) => setFilterAppt(e.target.value)}
                data-testid="leads-filter-appt"
                className="w-full px-2 py-1.5 text-xs font-mono border border-[#DDE5D8] focus:border-[#1F4E2C] outline-none bg-white"
              >
                <option value="all">Any</option>
                <option value="today">Today</option>
                <option value="week">Next 7 days</option>
                <option value="month">Next 30 days</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>

            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569] mb-1">Category</div>
              <select
                value={filterLeadCat}
                onChange={(e) => setFilterLeadCat(e.target.value)}
                data-testid="leads-filter-lead-category"
                className="w-full px-2 py-1.5 text-xs font-mono border border-[#DDE5D8] focus:border-[#1F4E2C] outline-none bg-white"
              >
                <option value="all">All categories</option>
                {LEAD_ENQUIRY_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>

            <div className="md:col-span-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569] mb-1">Solar Consultant</div>
              <select
                value={filterSc}
                onChange={(e) => setFilterSc(e.target.value)}
                data-testid="leads-filter-sc"
                className="w-full px-2 py-1.5 text-xs font-mono border border-[#DDE5D8] focus:border-[#1F4E2C] outline-none bg-white"
              >
                <option value="all">All leads (SC + Non-SC)</option>
                <option value="any">— Any SC-referred lead —</option>
                {scUsers.map((u) => (
                  <option key={u.id} value={u.id}>{(u.full_name || u.name) || "(unnamed)"} · {u.phone || u.email}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* ── Table ─────────────────────────────────────────────── */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="leads-table">
            <thead className="bg-[#F4F6F1] border-b border-[#DDE5D8]">
              <tr>
                <th className="px-3 py-2 text-left"><SortHeader k="created_at">Created</SortHeader></th>
                <th className="px-3 py-2 text-left"><SortHeader k="name">Lead</SortHeader></th>
                <th className="px-3 py-2 text-left"><SortHeader k="customer">Customer</SortHeader></th>
                <th className="px-3 py-2 text-left font-mono text-[9.5px] uppercase tracking-[0.2em] text-[#475569]">Category</th>
                <th className="px-3 py-2 text-left font-mono text-[9.5px] uppercase tracking-[0.2em] text-[#475569]">Type</th>
                <th className="px-3 py-2 text-left font-mono text-[9.5px] uppercase tracking-[0.2em] text-[#475569]">Source</th>
                <th className="px-3 py-2 text-right"><SortHeader k="power" className="justify-end w-full">Power</SortHeader></th>
                <th className="px-3 py-2 text-left"><SortHeader k="category">Temp</SortHeader></th>
                <th className="px-3 py-2 text-left"><SortHeader k="status">Status</SortHeader></th>
                <th className="px-3 py-2 text-left font-mono text-[9.5px] uppercase tracking-[0.2em] text-[#475569]">Assigned</th>
                <th className="px-3 py-2 text-right font-mono text-[9.5px] uppercase tracking-[0.2em] text-[#475569]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((l) => {
                const emp = employees.find((e) => e.id === l.sales_employee_id);
                let s = l.source || "crm";
                if (s === "public_survey") s = "campaign";
                const isCampaign = s === "campaign";
                const sc = l.sc_user_id ? scMap[l.sc_user_id] : null;
                const leadType = l.lead_type || derive_lead_type_fe(l.lead_category);
                const CatIcon = LEAD_ENQUIRY_CATEGORIES.find((c) => c.value === l.lead_category)?.Icon || HelpCircle;
                return (
                  <tr
                    key={l.id}
                    onClick={() => { setDetailLead(l); setDetailTab("details"); }}
                    data-testid={`lead-row-${l.id}`}
                    className="border-b border-[#EEF2EA] hover:bg-[#F4F6F1] cursor-pointer transition-colors"
                  >
                    <td className="px-3 py-2 align-top">
                      <div className="font-mono text-[11px] text-[#0F1A12]">{fmtDate(l.created_at)}</div>
                      <div className="font-mono text-[9.5px] text-[#94A3B8] mt-0.5">{relativeTime(l.created_at)}</div>
                    </td>
                    <td className="px-3 py-2 align-top max-w-[240px]">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <div className="font-mono text-[10px] font-bold text-[#1F4E2C]" data-testid={`lead-code-${l.id}`}>{l.code || "—"}</div>
                        <NotServiceableBadge lead={l} />
                      </div>
                      <div className="font-display font-semibold text-[12px] text-[#0F1A12] leading-tight truncate" title={l.name}>{l.name}</div>
                      {isCampaign && l.appointment_at && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[9.5px] font-mono bg-emerald-50 text-emerald-800 px-1.5 py-0.5 border border-emerald-200">
                          <Calendar className="w-2.5 h-2.5" />
                          {new Date(l.appointment_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="text-[12px] text-[#0F1A12]">{l.customer_snapshot?.customer_name || "—"}</div>
                      <div className="font-mono text-[9.5px] text-[#475569] mt-0.5">{l.customer_snapshot?.customer_type?.replace("_", " ")}</div>
                    </td>
                    <td className="px-3 py-2 align-top" data-testid={`lead-category-cell-${l.id}`}>
                      <div className="inline-flex items-center gap-1 text-[10.5px] text-[#0F1A12]"><CatIcon className="w-3 h-3 text-[#475569]" />{LEAD_CATEGORY_LABEL[l.lead_category] || l.lead_category || "—"}</div>
                    </td>
                    <td className="px-3 py-2 align-top" data-testid={`lead-type-cell-${l.id}`}>
                      <div className={"inline-flex px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider " + LEAD_TYPE_TONE[leadType]}>{LEAD_TYPE_LABEL[leadType]}</div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <StatusPill value={SOURCE_LABEL[s] || s} tone={isCampaign ? "success" : "info"} />
                      {sc && (
                        <div
                          className="mt-1 inline-flex items-center gap-1 text-[9.5px] font-mono bg-purple-50 text-purple-800 px-1.5 py-0.5 border border-purple-200"
                          data-testid={`lead-sc-${l.id}`}
                          title={`Referred by SC · ${sc.phone || ""}`}
                        >
                          <UserCheck className="w-2.5 h-2.5" /> SC: {sc.full_name}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top text-right font-mono text-[11px] text-[#0F1A12]">
                      {l.avg_power_consumption || 0}<span className="text-[#94A3B8]"> kWh</span>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <StatusPill value={l.category} tone={CATEGORY_TONE[l.category]} />
                    </td>
                    <td className="px-3 py-2 align-top" onClick={(e) => e.stopPropagation()}>
                      {l.status === "converted" ? (
                        <Link
                          to={l.follow_on_type === "quotation" ? `/crm/quotations/${l.follow_on_id}` : `/crm/opportunities/${l.follow_on_id || l.converted_opportunity_id}`}
                          className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-[#1F4E2C] hover:underline"
                          data-testid={`row-view-followon-${l.id}`}
                        >
                          <span className={`w-2 h-2 rounded-full ${STATUS_DOT.converted}`}></span>
                          {l.follow_on_code || "Converted"} · {followOnLabel(l) || "→"}
                        </Link>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${STATUS_DOT[l.status]}`}></span>
                          <select
                            value={l.status}
                            onChange={(e) => changeStatus(l, e.target.value)}
                            data-testid={`row-status-${l.id}`}
                            className="text-[10px] font-mono uppercase tracking-wider bg-transparent border border-transparent hover:border-[#DDE5D8] focus:border-[#1F4E2C] outline-none px-1.5 py-0.5 cursor-pointer"
                          >
                            {STATUSES.filter((s) => s !== "converted").map((s) => (
                              <option key={s} value={s}>{s.replace("_", " ")}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="font-mono text-[10px] text-[#0F1A12]">{emp?.name || <span className="text-[#94A3B8] italic">Unassigned</span>}</div>
                      {emp?.territory && <div className="font-mono text-[9.5px] text-[#94A3B8] mt-0.5">{emp.territory}</div>}
                    </td>
                    <td className="px-3 py-2 align-top text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex items-center gap-1">
                        {l.status !== "converted" && (leadType === "product_enquiry" ? (() => {
                          const canQuote = l.status === "accepted";
                          return (
                            <button
                              onClick={() => createQuotationFromLead(l)}
                              disabled={!canQuote}
                              data-testid={`row-create-quote-${l.id}`}
                              title={canQuote ? "Create quotation" : "Mark lead 'Accepted' first"}
                              className={"text-[9.5px] font-mono uppercase tracking-wider px-2 py-1 inline-flex items-center gap-1 transition-colors " +
                                (canQuote
                                  ? "bg-purple-700 text-white hover:bg-purple-900"
                                  : "bg-[#EEF2EA] text-[#94A3B8] cursor-not-allowed")}
                            >
                              Quote <FileSignature className="w-3 h-3" />
                            </button>
                          );
                        })() : (() => {
                          const canConvert = l.status === "accepted" && l.sales_employee_id;
                          const reason = !canConvert
                            ? (l.status !== "accepted" ? "Mark lead 'Accepted' first" : "Assign a Sales Employee first")
                            : "Convert to opportunity";
                          return (
                            <button
                              onClick={() => convert(l)}
                              disabled={!canConvert}
                              data-testid={`row-convert-${l.id}`}
                              title={reason}
                              className={"text-[9.5px] font-mono uppercase tracking-wider px-2 py-1 inline-flex items-center gap-1 transition-colors " +
                                (canConvert
                                  ? "bg-[#1F4E2C] text-white hover:bg-[#0F1A12]"
                                  : "bg-[#EEF2EA] text-[#94A3B8] cursor-not-allowed")}
                            >
                              Convert <ArrowRightCircle className="w-3 h-3" />
                            </button>
                          );
                        })())}
                        <button
                          onClick={() => remove(l)}
                          title="Delete"
                          data-testid={`row-delete-${l.id}`}
                          className="p-1 text-[#475569] hover:text-red-600"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {paged.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center text-sm font-mono text-[#94A3B8]">
                    {leads.length === 0 ? "No leads yet." : "No leads match your filters."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          pageSize={pageSize}
          total={sorted.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          testid="leads-pagination"
        />
      </div>

      {/* ── Create modal (unchanged) ─────────────────────────── */}
      <Modal open={open} onClose={() => setOpen(false)} title="New Lead" wide>
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="lead-form">
          <Field label="Lead Name *">
            <TextInput required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name" />
          </Field>
          <Field label="Customer *" className="md:col-span-2">
            <div className="flex items-center gap-1 mb-2">
              <button
                type="button"
                onClick={() => setCustomerMode("existing")}
                data-testid="lead-customer-mode-existing"
                className={"px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest border " + (customerMode === "existing" ? "bg-[#1F4E2C] text-white border-[#1F4E2C]" : "border-[#DDE5D8] text-[#475569]")}
              >
                Existing Customer
              </button>
              <button
                type="button"
                onClick={() => setCustomerMode("new")}
                data-testid="lead-customer-mode-new"
                className={"px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest border " + (customerMode === "new" ? "bg-[#1F4E2C] text-white border-[#1F4E2C]" : "border-[#DDE5D8] text-[#475569]")}
              >
                New Customer
              </button>
            </div>
            {customerMode === "existing" ? (
              <CustomerPicker
                selected={selectedCustomer}
                onSelect={(c) => { setForm({ ...form, customer_id: c.id }); setSelectedCustomer(c); }}
                placeholder="Search customer by name, phone or email…"
                testid="lead-customer"
                required
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-[#F4F6F1] p-3">
                <Field label="Customer Name *">
                  <TextInput required value={newCust.customer_name} onChange={(e) => setNewCust({ ...newCust, customer_name: e.target.value })} data-testid="new-cust-name" />
                </Field>
                <Field label="Customer Type *">
                  <Select value={newCust.customer_type} onChange={(e) => setNewCust({ ...newCust, customer_type: e.target.value })} data-testid="new-cust-type">
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="housing_society">Housing Society</option>
                  </Select>
                </Field>
                <Field label="Phone *">
                  <TextInput required value={newCust.phone} onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })} data-testid="new-cust-phone" />
                </Field>
                <Field label="Email">
                  <TextInput type="email" value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })} data-testid="new-cust-email" />
                </Field>
                <Field label="Address" className="md:col-span-2">
                  <TextInput value={newCust.address} onChange={(e) => setNewCust({ ...newCust, address: e.target.value })} data-testid="new-cust-address" />
                </Field>
                <Field label="PIN Code *">
                  <TextInput required maxLength={6} value={newCust.pin_code} onChange={(e) => setNewCust({ ...newCust, pin_code: e.target.value.replace(/\D/g, "").slice(0, 6) })} placeholder="560001" data-testid="new-cust-pin" />
                </Field>
                <div className="md:col-span-2 text-[10px] font-mono text-[#475569]">
                  If this phone/email matches an existing customer, their already-registered PIN is used —
                  a non-serviceable area will block Lead creation entirely. Otherwise a new Customer is
                  created and the Lead is simply flagged "Not Serviceable" if the area isn't covered yet.
                </div>
              </div>
            )}
          </Field>
          {customerMode === "existing" && selectedCustomer && (
            <div className="md:col-span-2 bg-[#F4F6F1] p-3 text-xs font-mono grid grid-cols-3 gap-3">
              <div><div className="text-[#475569]">Phone</div>{selectedCustomer.phone}</div>
              <div><div className="text-[#475569]">Email</div>{selectedCustomer.email}</div>
              <div><div className="text-[#475569]">Type</div>{selectedCustomer.customer_type}</div>
            </div>
          )}
          <Field label="Category *" testid="lead-category-field">
            <Select value={form.lead_category} onChange={(e) => setForm({ ...form, lead_category: e.target.value })} data-testid="lead-category-select">
              {LEAD_ENQUIRY_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </Select>
            <div className={"mt-1 inline-flex px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider " + LEAD_TYPE_TONE[derive_lead_type_fe(form.lead_category)]}>
              {LEAD_TYPE_LABEL[derive_lead_type_fe(form.lead_category)]}
            </div>
          </Field>
          {form.lead_category === "others" && (
            <Field label="Please specify *" className="md:col-span-2">
              <TextInput required value={form.lead_category_other_text} onChange={(e) => setForm({ ...form, lead_category_other_text: e.target.value })} data-testid="lead-category-other-text" placeholder="What is this enquiry about?" />
            </Field>
          )}
          <Field label="Avg Power (kWh / month)">
            <TextInput type="number" value={form.avg_power_consumption} onChange={(e) => setForm({ ...form, avg_power_consumption: e.target.value })} />
          </Field>
          <Field label="Expected Installation Date">
            <TextInput type="date" value={form.expected_installation_date} onChange={(e) => setForm({ ...form, expected_installation_date: e.target.value })} />
          </Field>
          <Field label="Temperature">
            <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </Field>
          <Field label="Assign Sales Employee">
            <Select value={form.sales_employee_id} onChange={(e) => setForm({ ...form, sales_employee_id: e.target.value })} data-testid="lead-assign">
              <option value="">— Unassigned —</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name} · {e.territory}</option>)}
            </Select>
          </Field>
          <Field label="Notes" className="md:col-span-2">
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
          <div className="md:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-[#475569]">Cancel</button>
            <PrimaryButton type="submit" disabled={saving} testid="lead-save-btn">{saving ? "Saving…" : "Create lead"}</PrimaryButton>
          </div>
        </form>
      </Modal>

      {/* ── Detail modal ─────────────────────────────────────── */}
      <Modal open={!!detailLead} onClose={() => setDetailLead(null)} title={detailLead?.name || "Lead"} wide>
        {detailLead && (() => {
          const cust = customers.find((c) => c.id === detailLead.customer_id);
          const emp = employees.find((e) => e.id === detailLead.sales_employee_id);
          const sc = detailLead.sc_user_id ? scMap[detailLead.sc_user_id] : null;
          let sourceKey = detailLead.source || "crm";
          if (sourceKey === "public_survey") sourceKey = "campaign";
          const isCampaign = sourceKey === "campaign";
          return (
            <div className="space-y-5" data-testid="lead-detail-modal">
              <div className="flex items-center gap-1 border-b border-[#DDE5D8] -mt-1">
                {[
                  { v: "details", l: "Details" },
                  { v: "whatsapp", l: "WhatsApp" },
                ].map((t) => (
                  <button
                    key={t.v}
                    onClick={() => setDetailTab(t.v)}
                    data-testid={`lead-detail-tab-${t.v}`}
                    className={"px-3 py-2 text-[11px] font-mono uppercase tracking-widest border-b-2 -mb-px flex items-center gap-1.5 " +
                      (detailTab === t.v ? "border-[#1F4E2C] text-[#1F4E2C]" : "border-transparent text-[#94A3B8] hover:text-[#475569]")}
                  >
                    {t.v === "whatsapp" && <MessageCircle className="w-3.5 h-3.5" />} {t.l}
                  </button>
                ))}
              </div>

              {detailTab === "whatsapp" ? (
                <WhatsAppTracker leadId={detailLead.id} />
              ) : (
              <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[11px] font-bold text-[#1F4E2C] bg-[#EEF2EA] px-2 py-1" data-testid="lead-detail-code">{detailLead.code || "—"}</span>
                <StatusPill value={detailLead.status.replace("_", " ")} tone={STATUS_TONE[detailLead.status]} />
                <StatusPill value={detailLead.category} tone={CATEGORY_TONE[detailLead.category]} />
                <StatusPill value={LEAD_CATEGORY_LABEL[detailLead.lead_category] || detailLead.lead_category} tone="info" />
                <div className={"inline-flex px-2 py-1 rounded text-[10px] font-mono uppercase tracking-wider " + LEAD_TYPE_TONE[detailLead.lead_type || derive_lead_type_fe(detailLead.lead_category)]}>
                  {LEAD_TYPE_LABEL[detailLead.lead_type || derive_lead_type_fe(detailLead.lead_category)]}
                </div>
                <StatusPill value={SOURCE_LABEL[sourceKey] || sourceKey} tone={isCampaign ? "success" : "info"} />
                <NotServiceableBadge lead={detailLead} />
                {sc && (
                  <div
                    className="inline-flex items-center gap-1.5 text-[11px] font-mono bg-purple-50 text-purple-900 px-2 py-1 border border-purple-200"
                    data-testid="lead-detail-sc-badge"
                    title={`SC phone: ${sc.phone || "—"} · email: ${sc.email}`}
                  >
                    <UserCheck className="w-3 h-3" /> Referred by <span className="font-semibold">{sc.full_name}</span>
                  </div>
                )}
                <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">
                  Created {new Date(detailLead.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>

              {isCampaign && detailLead.appointment_at && (
                <div className="bg-emerald-50 border border-emerald-200 px-4 py-3 flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-emerald-700" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-700">Site visit booked</div>
                    <div className="font-display text-lg font-semibold text-emerald-900">
                      {new Date(detailLead.appointment_at).toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-5">
                <section className="border border-[#DDE5D8] p-4">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#475569] flex items-center gap-1.5">
                    {cust?.customer_type === "residential" ? <User className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
                    Customer
                  </div>
                  <div className="font-display text-lg font-semibold mt-1">
                    {cust?.customer_name || detailLead.customer_snapshot?.customer_name || "—"}
                  </div>
                  <div className="text-xs font-mono text-[#475569] mt-0.5">
                    {(cust?.customer_type || detailLead.customer_snapshot?.customer_type || "").replace("_", " ")}
                  </div>
                  <div className="mt-3 space-y-1.5 text-xs">
                    <div className="flex items-center gap-2">
                      <Phone className="w-3 h-3 text-[#94A3B8]" />
                      <a href={`tel:${cust?.phone || detailLead.customer_snapshot?.phone || ""}`} className="font-mono hover:text-[#1F4E2C]">
                        {cust?.phone || detailLead.customer_snapshot?.phone || "—"}
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-3 h-3 text-[#94A3B8]" />
                      <a href={`mailto:${cust?.email || detailLead.customer_snapshot?.email || ""}`} className="font-mono hover:text-[#1F4E2C] truncate">
                        {cust?.email || detailLead.customer_snapshot?.email || "—"}
                      </a>
                    </div>
                    {cust?.address && (
                      <div className="flex items-start gap-2">
                        <MapPin className="w-3 h-3 text-[#94A3B8] mt-0.5" />
                        <span className="font-mono text-[#475569]">{cust.address}</span>
                      </div>
                    )}
                    {cust?.consumer_name && cust.consumer_name !== cust.customer_name && (
                      <div className="mt-2 pt-2 border-t border-[#EEF2EA]">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8]">Contact person</div>
                        <div className="font-mono text-[#0F1A12] mt-0.5">{cust.consumer_name}</div>
                        {cust.consumer_phone && <div className="font-mono text-[#475569] text-[11px]">{cust.consumer_phone}</div>}
                      </div>
                    )}
                  </div>
                </section>

                <section className="border border-[#DDE5D8] p-4">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#475569] flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" /> Lead facts
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-y-3 gap-x-4 text-xs">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">Avg power</div>
                      <div className="font-display text-lg font-semibold mt-0.5">{detailLead.avg_power_consumption} <span className="text-xs font-normal">kWh</span></div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">Install by</div>
                      <div className="font-mono text-[#0F1A12] mt-0.5">{detailLead.expected_installation_date || "Not set"}</div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">Assigned to</div>
                      <div className="font-mono text-[#0F1A12] mt-0.5">{emp?.name || "Unassigned"}</div>
                      {emp?.territory && <div className="text-[10px] text-[#94A3B8] mt-0.5">{emp.territory}</div>}
                    </div>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">Source</div>
                      <div className="font-mono text-[#0F1A12] mt-0.5">{SOURCE_LABEL[sourceKey] || sourceKey}</div>
                    </div>
                    {sc && (
                      <div className="col-span-2 pt-2 mt-1 border-t border-dashed border-[#DDE5D8]">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">Referred by Solar Consultant</div>
                        <div className="font-display text-base font-semibold text-[#0F1A12] mt-0.5">{sc.full_name}</div>
                        <div className="font-mono text-[11px] text-[#475569] flex items-center gap-3 mt-0.5">
                          {sc.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{sc.phone}</span>}
                          {sc.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{sc.email}</span>}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-[#EEF2EA]">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] mb-1.5">Reassign</div>
                    <Select
                      value={detailLead.sales_employee_id || ""}
                      onChange={async (e) => {
                        const newId = e.target.value;
                        try {
                          await api.put(`/leads/${detailLead.id}/assign?sales_employee_id=${newId}`);
                          toast.success("Reassigned");
                          reload();
                          setDetailLead({ ...detailLead, sales_employee_id: newId });
                        } catch (err) {
                          toast.error(formatApiError(err.response?.data?.detail) || err.message);
                        }
                      }}
                      data-testid="detail-reassign"
                    >
                      <option value="">— Unassigned —</option>
                      {employees.map((ee) => <option key={ee.id} value={ee.id}>{`${ee.name} · ${ee.territory}`}</option>)}
                    </Select>
                  </div>
                </section>
              </div>

              <section className="border border-[#DDE5D8] p-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#475569] flex items-center gap-1.5 mb-2">
                  <FileText className="w-3 h-3" /> Notes
                </div>
                {detailLead.lead_category === "others" && detailLead.lead_category_other_text && (
                  <div className="mb-2 text-xs font-mono text-[#0F1A12] bg-purple-50 border border-purple-200 px-3 py-2">
                    <span className="text-purple-800 font-semibold">Enquiry details:</span> {detailLead.lead_category_other_text}
                  </div>
                )}
                <pre className="text-xs font-mono text-[#0F1A12] whitespace-pre-wrap leading-relaxed bg-[#F4F6F1] p-3 max-h-48 overflow-auto">
                  {detailLead.notes || "—"}
                </pre>
              </section>

              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#DDE5D8]">
                {STATUSES.filter((s) => s !== "converted").map((s) => (
                  <button
                    key={s}
                    onClick={() => changeStatus(detailLead, s)}
                    disabled={detailLead.status === s || detailLead.status === "converted"}
                    data-testid={`detail-status-${s}`}
                    className={"text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 border " +
                      (detailLead.status === s
                        ? "bg-[#0F1A12] text-white border-[#0F1A12]"
                        : "border-[#DDE5D8] text-[#475569] hover:border-[#0F1A12]") +
                      (detailLead.status === "converted" ? " opacity-40 cursor-not-allowed" : "")}>
                    {s.replace("_", " ")}
                  </button>
                ))}
                {detailLead.status !== "converted" ? (
                  (detailLead.lead_type || derive_lead_type_fe(detailLead.lead_category)) === "product_enquiry" ? (() => {
                    const canQuote = detailLead.status === "accepted";
                    return (
                      <button
                        onClick={async () => { await createQuotationFromLead(detailLead); setDetailLead(null); }}
                        disabled={!canQuote}
                        title={canQuote ? "" : "Mark lead 'Accepted' first"}
                        data-testid="detail-create-quote-btn"
                        className={"ml-auto px-4 py-2 text-[10px] font-mono uppercase tracking-widest flex items-center gap-2 transition-colors " +
                          (canQuote
                            ? "bg-purple-700 text-white hover:bg-purple-900"
                            : "bg-[#EEF2EA] text-[#94A3B8] cursor-not-allowed")}
                      >
                        Create quotation <FileSignature className="w-3 h-3" />
                      </button>
                    );
                  })() : (() => {
                    const canConvert = detailLead.status === "accepted" && detailLead.sales_employee_id;
                    const reason = !canConvert
                      ? (detailLead.status !== "accepted" ? "Mark lead 'Accepted' first" : "Assign a Sales Employee first")
                      : "";
                    return (
                      <button
                        onClick={async () => { await convert(detailLead); setDetailLead(null); }}
                        disabled={!canConvert}
                        title={reason}
                        data-testid="detail-convert-btn"
                        className={"ml-auto px-4 py-2 text-[10px] font-mono uppercase tracking-widest flex items-center gap-2 transition-colors " +
                          (canConvert
                            ? "bg-[#1F4E2C] text-white hover:bg-[#0F1A12]"
                            : "bg-[#EEF2EA] text-[#94A3B8] cursor-not-allowed")}
                      >
                        Convert to opportunity <ArrowRightCircle className="w-3 h-3" />
                      </button>
                    );
                  })()
                ) : (
                  <Link
                    to={detailLead.follow_on_type === "quotation" ? `/crm/quotations/${detailLead.follow_on_id}` : `/crm/opportunities/${detailLead.follow_on_id || detailLead.converted_opportunity_id}`}
                    onClick={() => setDetailLead(null)}
                    className="ml-auto bg-[#0F1A12] text-white px-4 py-2 text-[10px] font-mono uppercase tracking-widest hover:bg-[#1F4E2C] transition-colors flex items-center gap-2"
                    data-testid="detail-view-opp-link"
                  >
                    View {detailLead.follow_on_type === "quotation" ? "quotation" : "opportunity"} <ArrowRightCircle className="w-3 h-3" />
                  </Link>
                )}
              </div>
              </>
              )}
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}
